import React, { useEffect, useRef, useState } from "react";
import { createUseStyles } from "react-jss";
import { getBackgroundPositionByFocusPoint } from "../helpers/helpers";
import { BackgroundImage } from "./BackgroundImage";
import { BackgroundVideo } from "./BackgroundVideo";
import { computeKenBurnsStyle } from "./kenBurnsCalculator";
import { KenBurnsFocusEffectOverlay } from "./KenBurnsFocusEffectOverlay";

const useStyles = createUseStyles({
  singleBackground: {
    position: "sticky",
    top: 0,
    backgroundColor: ({ backgroundColor }) => (backgroundColor ? backgroundColor : ""),
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    backgroundPosition: "center",
    minHeight: ({ minHeight }) => minHeight,
    width: "100%",
    height: ({ height, minHeight }) => height || minHeight,
    overflow: "hidden",
    backgroundPositionX: ({ backgroundPositionX }) => backgroundPositionX + "%",
    backgroundPositionY: ({ backgroundPositionY }) => backgroundPositionY + "%",
  },
  kenBurnsWrapper: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
});

export const SingleBackground = ({
  backgroundImage,
  backgroundVideo,
  gradient,
  isAdminMode,
  backgroundColor,
  height,
  minHeight,
  backgroundFocus,
  kenBurns,
  activeStepKey,
  priority = false,
}) => {
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const targetFocus = kenBurns || backgroundFocus;
  const [displayFocus, setDisplayFocus] = useState(targetFocus);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevStepKeyRef = useRef(activeStepKey);
  const prevFocusRef = useRef(targetFocus);
  const timerRef = useRef(null);

  useEffect(() => {
    const prevKey = prevStepKeyRef.current;
    const prevFocus = prevFocusRef.current;

    prevStepKeyRef.current = activeStepKey;
    prevFocusRef.current = targetFocus;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const isStepChange =
      Boolean(activeStepKey) &&
      Boolean(prevKey) &&
      activeStepKey !== prevKey &&
      activeStepKey !== "overview" &&
      prevKey !== "overview";

    const prevZoom = prevFocus?.zoom ?? 1;
    const currentTargetZoom = targetFocus?.zoom ?? 1;
    const isBothZoomed = prevZoom > 1.05 && currentTargetZoom > 1.05;

    if (isStepChange && isBothZoomed && !prefersReducedMotion) {
      // Stage 1: Zoom out to default 1.0x while gliding to the target focal point
      setIsTransitioning(true);
      setDisplayFocus({
        x: targetFocus?.x ?? 0,
        y: targetFocus?.y ?? 0,
        zoom: 1.0,
      });

      // Stage 2: After 550ms, zoom in to target zoom
      timerRef.current = setTimeout(() => {
        setDisplayFocus(targetFocus);
        setIsTransitioning(false);
        timerRef.current = null;
      }, 550);
    } else {
      setIsTransitioning(false);
      setDisplayFocus(targetFocus);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [targetFocus, activeStepKey, prefersReducedMotion]);

  // Use only backgroundFocus for object-position to eliminate 0ms jump when switching containers
  const { backgroundPositionX, backgroundPositionY } =
    getBackgroundPositionByFocusPoint(backgroundFocus?.x, backgroundFocus?.y);

  const classes = useStyles({
    gradient,
    backgroundColor,
    backgroundImage,
    height,
    isAdminMode,
    minHeight,
    backgroundPositionX,
    backgroundPositionY,
  });

  const kenBurnsStyle = computeKenBurnsStyle(
    displayFocus,
    prefersReducedMotion
  );

  const isEffectActive =
    !isTransitioning &&
    activeStepKey !== "overview" &&
    Boolean(displayFocus?.effect && displayFocus.effect !== "none");

  return (
    <div className={classes.singleBackground}>
      <div className={classes.kenBurnsWrapper} style={kenBurnsStyle} data-testid="kenburns-wrapper">
        {backgroundVideo ? (
          <BackgroundVideo videoUrl={backgroundVideo} gradient={gradient} />
        ) : (
          <BackgroundImage
            backgroundImage={backgroundImage}
            positionX={backgroundPositionX}
            positionY={backgroundPositionY}
            gradient={gradient}
            priority={priority}
          />
        )}
      </div>
      <KenBurnsFocusEffectOverlay
        focus={displayFocus}
        active={isEffectActive}
        prefersReducedMotion={prefersReducedMotion}
      />
    </div>
  );
};
